import type {
  CloudpackConfig,
  PackageDefinitionTransform,
  PackageDefinitionsCache,
  PackageJson,
  PackageJsonExports,
  GeneratedExports,
  RenderedRoute,
  InternalBundlerCapabilitiesOptions,
} from '@ms-cloudpack/common-types';
import { addExportsMapEntry, getDependencies, getExportsMap, expandExportsMap } from '@ms-cloudpack/package-utilities';
import { slash } from '@ms-cloudpack/path-string-parsing';
import { getPackageSettings, type GetPackageSettingsResult } from '../packageSettings/getPackageSettings.js';
import { mergePackageSettings } from '../packageSettings/mergePackageSettings.js';

/**
 * Creates a transform to be registered with a PackageDefinitions registry for updating package.json
 * based on package settings from the config.
 */
export function createPackageSettingsTransform(config: CloudpackConfig): PackageDefinitionTransform {
  return async ({ definition, packagePath, packages }) => {
    const { name, version } = definition;

    const { userPackageSettings, generatedPackageSettings } = getPackageSettings({
      name,
      version,
      userPackageSettings: config.packageSettings,
      includeBuiltIn: true,
      generatedPackageSettings: config.generated.packageSettings,
    });

    // Pull out package settings properties which will be fully processed by this transform.
    const { exports, includedDependencies, excludedDependencies, ...settings } = mergePackageSettings([
      // NOTE: If we add any generated settings in the future where the user settings need to
      // take precedence (such as `bundler` maybe), this merging will need to be adjusted.
      userPackageSettings || {},
      generatedPackageSettings || {},
    ]);

    // Calculate exports based on settings
    definition.exports = await computeExports(
      { packagePath, definition, userPackageSettings, generatedPackageSettings },
      { packages, config },
    );

    updateDependencies({
      definition,
      userPackageSettings,
      generatedPackageSettings,
      excludedDependencies,
    });

    // Store the unprocessed settings plus the mode
    definition.cloudpack = {
      ...definition.cloudpack, // copy anything set by other transforms
      mode: config.mode,
      ...settings,
    };

    return definition;
  };
}

function updateDependencies(
  options: Pick<GetPackageSettingsResult, 'userPackageSettings' | 'generatedPackageSettings'> & {
    definition: PackageJson;
    /** Merged `excludedDependencies` from the user, built-in, and generated configs */
    excludedDependencies: string[] | undefined;
  },
): void {
  const { definition, userPackageSettings, generatedPackageSettings } = options;

  // Get merged dependencies in the proper order of precedence, for determining included dep versions.
  const allDependencies = getDependencies(definition, { includeAll: true });

  const prodAndPeerDependencies = getDependencies(definition);

  // If we have included dependencies, add them.
  const userIncludedDependencies = [...(userPackageSettings?.includedDependencies || [])];
  const generatedIncludedDependencies = [...(generatedPackageSettings?.includedDependencies || [])];

  // Expand the $devDependencies placeholder to the actual dev dependencies.
  const includeDevDependenciesIndex = userIncludedDependencies.indexOf('$devDependencies');
  if (includeDevDependenciesIndex !== -1 && definition.devDependencies) {
    userIncludedDependencies.splice(includeDevDependenciesIndex, 1);
    definition.dependencies = {
      ...definition.dependencies,
      ...definition.devDependencies,
    };
  }

  // For each included dependency, if it's not already listed, add it using the version from
  // other dep types, or use a wildcard.
  for (const dep of userIncludedDependencies) {
    if (!prodAndPeerDependencies[dep]) {
      definition.dependencies ??= {};
      definition.dependencies[dep] = allDependencies[dep] || '*';
    }
  }

  // Add generated included dependencies to peer dependencies as they are ignored if missing.
  // We don't want to add them to dependencies, since if they are ever not required, they will error resolving.
  for (const dep of generatedIncludedDependencies) {
    if (!prodAndPeerDependencies[dep]) {
      definition.peerDependencies ??= {};
      definition.peerDependencies[dep] = allDependencies[dep] || '*';
    }
  }

  const excludedDependencies = [...(options.excludedDependencies || [])];

  // If the $peerDependencies placeholder is present, remove all peer dependencies.
  const excludePeerDependenciesIndex = excludedDependencies.indexOf('$peerDependencies');
  if (excludePeerDependenciesIndex !== -1) {
    excludedDependencies.splice(excludePeerDependenciesIndex, 1);
    delete definition.peerDependencies;
    delete definition.peerDependenciesMeta;
  }

  // Delete the excluded deps from dependencies and peerDependencies.
  // We ignore dev and optional deps by default, so it's probably not necessary to delete from there.
  for (const dep of excludedDependencies) {
    delete definition.dependencies?.[dep];
    delete definition.peerDependencies?.[dep];
  }
}

async function computeExports(
  options: GetPackageSettingsResult & {
    packagePath: string;
    definition: PackageJson;
  },
  context: { packages: PackageDefinitionsCache; config: CloudpackConfig },
): Promise<PackageJsonExports | undefined> {
  const { packagePath, definition, userPackageSettings, generatedPackageSettings } = options;
  const { appPath, routes, features } = context.config;
  const bundlerCapabilities = (userPackageSettings?.bundlerCapabilities || {}) as InternalBundlerCapabilitiesOptions;
  const resolveWebExtensions =
    bundlerCapabilities['resolve-web-extensions'] ??
    // eslint-disable-next-line etc/no-deprecated
    !!features?.resolveWebExtensions;

  // If this is the app package and it has routes with entries, use those to create the exports map.
  if (slash(appPath) === slash(packagePath) && routes?.length) {
    const routeExports: GeneratedExports = {};

    for (const route of routes) {
      const { entry = [], serverEntry } = route as RenderedRoute;

      for (const entryItem of entry) {
        await addExportsMapEntry(
          {
            exports: routeExports,
            packagePath,
            environmentCondition: 'browser',
            filePath: entryItem.sourcePath,
            importPath: entryItem.importPath,
            resolveWebExtensions,
          },
          context,
        );
      }

      if (serverEntry) {
        await addExportsMapEntry(
          {
            exports: routeExports,
            packagePath,
            environmentCondition: 'node',
            filePath: serverEntry.sourcePath,
            importPath: serverEntry.importPath,
            resolveWebExtensions,
          },
          context,
        );
      }
    }

    if (Object.keys(routeExports).length) {
      // Currently route exports take precedence over all other exports for the app package.
      return routeExports;
    }
  }

  // TODO: an array of export objects is not really valid per the spec and most implementations
  const exports: PackageJsonExports[] = [];

  // Push exports from the package.
  if (userPackageSettings?.exports !== undefined) {
    // Prefer user settings over the original definition, including respecting null (remove exports).
    if (userPackageSettings.exports !== null) {
      exports.push(expandExportsMap(userPackageSettings.exports, packagePath));
    }
  } else if (definition.exports !== undefined) {
    // Or take the original exports if specified (again respecting null)
    if (definition.exports !== null) {
      exports.push(definition.exports);
    }
  } else {
    // Otherwise, generate an exports map from any other given entry points.
    exports.push(await getExportsMap({ packagePath, disableTransforms: true, resolveWebExtensions }, context));
  }

  // Push generated exports.
  if (generatedPackageSettings?.exports) {
    exports.push(generatedPackageSettings.exports);
  }

  if (exports.length === 0) {
    // If nothing was pushed to the array, this means the exports from either user package settings
    // or the original definition must have been null (and we have no route or generated exports),
    // so respect that.
    return null;
  }

  if (exports.length === 1) {
    return exports[0];
  }

  return exports;
}
